// Utility functions for downloading badges as images
import html2canvas from 'html2canvas';

export const downloadBadgeAsImage = async (badgeUrl: string, filename: string = 'session-badge.png') => {
  try {
    // Create a hidden iframe to load the badge
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    iframe.style.top = '-9999px';
    iframe.style.width = '1200px';
    iframe.style.height = '800px';
    iframe.src = badgeUrl;
    
    document.body.appendChild(iframe);
    
    // Wait for iframe to load
    iframe.onload = () => {
      setTimeout(async () => {
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
          if (!iframeDoc) {
            throw new Error('Cannot access iframe content');
          }
          
          // Find the badge container in the iframe
          const badgeContainer = iframeDoc.getElementById('badge-container') || 
                               iframeDoc.getElementById('banner-container');
          
          if (badgeContainer) {
            // Use html2canvas to capture the element
            const canvas = await html2canvas(badgeContainer, {
              backgroundColor: null,
              scale: 2, // Higher quality
              useCORS: true,
              allowTaint: true,
              logging: false,
              width: badgeContainer.offsetWidth,
              height: badgeContainer.offsetHeight
            });
            
            // Convert canvas to blob and download
            canvas.toBlob((blob) => {
              if (blob) {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                
                // Clean up iframe
                document.body.removeChild(iframe);
              } else {
                throw new Error('Failed to create image blob');
              }
            }, 'image/png', 1.0);
            
          } else {
            throw new Error('Badge container not found');
          }
        } catch (error) {
          console.error('Failed to capture badge:', error);
          // Clean up iframe
          document.body.removeChild(iframe);
          // Fallback to popup window with instructions
          fallbackToPopup(badgeUrl, filename);
        }
      }, 3000); // Wait 3 seconds for everything to load
    };
    
    // Handle iframe load error
    iframe.onerror = () => {
      document.body.removeChild(iframe);
      fallbackToPopup(badgeUrl, filename);
    };
    
  } catch (error) {
    console.error('Download failed:', error);
    fallbackToPopup(badgeUrl, filename);
  }
};

const fallbackToPopup = (badgeUrl: string, filename: string) => {
  // Fallback: open in popup with manual instructions
  const badgeWindow = window.open(
    badgeUrl, 
    '_blank', 
    'width=1200,height=800,scrollbars=no,resizable=yes,status=no,location=no,toolbar=no,menubar=no'
  );
  
  if (badgeWindow) {
    badgeWindow.onload = () => {
      setTimeout(() => {
        showDownloadInstructions(badgeWindow, filename);
      }, 1000);
    };
  }
};

const showDownloadInstructions = (badgeWindow: Window, filename: string = 'session-badge.png') => {
  const instructions = badgeWindow.document.createElement('div');
  instructions.innerHTML = `
    <div style="position: fixed; top: 10px; right: 10px; background: white; padding: 15px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 1000; font-family: Arial, sans-serif; max-width: 300px;">
      <h3 style="margin: 0 0 10px 0; font-size: 16px; color: #00529B;">Download Badge</h3>
      <p style="margin: 0 0 15px 0; font-size: 13px; line-height: 1.4; color: #666;">
        To save this badge as an image:
      </p>
      <ol style="margin: 0 0 15px 0; padding-left: 20px; font-size: 12px; color: #666;">
        <li>Right-click on the badge</li>
        <li>Select "Save image as..." or "Copy image"</li>
        <li>Or take a screenshot (Cmd/Ctrl + Shift + S)</li>
      </ol>
      <div style="display: flex; gap: 8px;">
        <button onclick="window.print()" style="background: #00529B; color: white; border: none; padding: 8px 12px; border-radius: 4px; cursor: pointer; font-size: 12px; flex: 1;">Print</button>
        <button onclick="window.close()" style="background: #666; color: white; border: none; padding: 8px 12px; border-radius: 4px; cursor: pointer; font-size: 12px; flex: 1;">Close</button>
      </div>
    </div>
  `;
  badgeWindow.document.body.appendChild(instructions);
};

export const copyBadgeUrl = (url: string) => {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(url);
    return true;
  } else {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = url;
    document.body.appendChild(textArea);
    textArea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  }
};
